/**
 * play.thegamer.network player-side state.
 *
 * One root state object; reducer mutates in place and the renderer
 * recomputes from scratch each frame. Same pattern as the host SPA.
 */

export type ColorState = 'correct' | 'present' | 'absent';

export interface ScoredCell {
  letter: string;
  state: ColorState;
}

export interface PlayerSpaState {
  /** Set once player has typed platform + tag on first visit. */
  handle: string | null;
  platform: 'twitch' | 'youtube' | 'kick' | 'web' | null;
  tag: string | null;
  /** Connection status. */
  status: 'idle' | 'connecting' | 'connected' | 'closed' | 'error';
  closeReason: string | null;
  /** Round metadata pushed from the server. */
  roundActive: boolean;
  wordLength: number;
  maxTries: number;
  endsAtMs: number | null;
  /** Per-row scored cells (letters revealed). Empty when no guesses yet. */
  rows: ScoredCell[][];
  /** Letters currently being typed for the in-flight guess. */
  pending: string;
  /** Pre-round / post-round overlay text. */
  message: string | null;
  /** The final answer + your points after round-end. */
  lastResult: { word: string; points: number; solved: boolean } | null;
}

export function initialState(): PlayerSpaState {
  return {
    handle: null,
    platform: null,
    tag: null,
    status: 'idle',
    closeReason: null,
    roundActive: false,
    wordLength: 5,
    maxTries: 6,
    endsAtMs: null,
    rows: [],
    pending: '',
    message: null,
    lastResult: null,
  };
}

export function applyFrame(state: PlayerSpaState, frame: { type: string } & Record<string, unknown>): PlayerSpaState {
  switch (frame.type) {
    case 'sw_player_welcome':
      state.roundActive = !!frame.round_active;
      state.wordLength = Number(frame.word_length) || state.wordLength;
      state.maxTries = Number(frame.max_tries) || state.maxTries;
      state.endsAtMs = (frame.ends_at_ms as number | null) ?? null;
      state.message = state.roundActive ? null : 'Waiting for the next round…';
      return state;
    case 'sw_player_round_open':
      state.roundActive = true;
      state.wordLength = Number(frame.word_length) || state.wordLength;
      state.maxTries = Number(frame.max_tries) || state.maxTries;
      state.endsAtMs = (frame.ends_at_ms as number | null) ?? null;
      state.rows = [];
      state.pending = '';
      state.message = null;
      state.lastResult = null;
      return state;
    case 'sw_player_guess_result': {
      const row = (frame.row as ScoredCell[] | undefined) ?? [];
      state.rows.push(row);
      state.pending = '';
      if (frame.solved) {
        state.message = `Solved in ${frame.tries_used}!`;
      } else if (frame.failed) {
        state.message = `Out of tries — answer was ${String(frame.answer_if_finished ?? '?').toUpperCase()}`;
      }
      return state;
    }
    case 'sw_player_invalid_guess': {
      const reason = String(frame.reason ?? '');
      const friendly: Record<string, string> = {
        not_in_word_list: 'Not in word list',
        wrong_length: 'Wrong length',
        non_alphabetic: 'Letters only',
        not_string: 'Bad input',
        round_not_active: 'Round not active',
        no_active_round: 'No round running yet',
        already_finished: 'You already finished this round',
        rate_limited: 'Slow down a sec',
      };
      state.message = friendly[reason] ?? `Invalid guess: ${reason}`;
      return state;
    }
    case 'sw_player_round_end': {
      state.roundActive = false;
      const word = String(frame.word ?? '');
      const me = (frame.results as Array<{ user_id: string; solved: boolean; points: number }> | undefined)?.find(
        (r) => r.user_id === stateUserId(state),
      );
      state.lastResult = { word, points: me?.points ?? 0, solved: !!me?.solved };
      state.message = me?.solved
        ? `Solved! +${me.points} pts`
        : `Answer: ${word.toUpperCase()}`;
      return state;
    }
    default:
      return state;
  }
}

function stateUserId(state: PlayerSpaState): string {
  if (!state.platform || !state.tag) return '';
  return `${state.platform}:dn:${state.tag.toLowerCase()}`;
}
